import { forwardRef, type ReactNode, useEffect, useMemo, useState } from 'react';
import { unionBy } from 'es-toolkit';
import { type QuizGenerateItem } from '@/api/ai/type';
import { type AchievementStandard } from '@/shared/features/ai-quiz-generation/types';
import { BlankResult, BuildAiGeneratedResult, GenerateResultDescription } from '@/views/build/features/ai-quiz-result/components/GeneratedResult';
import { type GenerateStatus } from '@/views/build/modal/BuildAiModal/components/BuildAiGenerateAction';

/**
 * AI 퀴즈 생성 결과 섹션의 Props 그룹화
 */
interface BuildAiResultSectionProps {
  /** 생성 상태 */
  generateStatus: GenerateStatus;
  /** 생성된 아이템 목록 */
  generatedItems: QuizGenerateItem[];
  /** 생성 중 표시할 fallback UI */
  pendingFallback: ReactNode;
  /** 퀴즈 생성 개수 (skeleton 표시용) */
  quizCount: number;

  /** 아이템 선택 관련 상태 및 핸들러 그룹 */
  selection: {
    selectedItems: QuizGenerateItem[];
    savedItems: QuizGenerateItem[];
    unSavedItems: QuizGenerateItem[];
    onSelect: (item: QuizGenerateItem) => void;
    onSelectAll: () => void;
    onDelete: (item: QuizGenerateItem) => void;
  };

  /** 선택된 성취기준 */
  achievementStandards: AchievementStandard[];

  /** 추가 className */
  className?: string;
}

/**
 * AI 퀴즈 생성 결과 섹션
 *
 * renderResultSection 인라인 함수를 컴포넌트로 추출하여:
 * - 매 렌더마다 함수 재생성 방지
 * - 독립적으로 테스트 가능
 * - Props를 selection 그룹으로 정리
 *
 * **렌더링 케이스**:
 * 1. 재생성 중이고 아이템 없음 → pendingFallback
 * 2. 아이템 없음 → BlankResult
 * 3. 아이템 있음 → BuildAiGeneratedResult
 */
export const BuildAiResultSection = forwardRef<HTMLDivElement, BuildAiResultSectionProps>(({
  generateStatus,
  generatedItems,
  pendingFallback,
  quizCount,
  selection,
  achievementStandards,
  className
}, ref) => {
  /**
   * 생성된 퀴즈 아이템의 성취기준 매핑을 위한 히스토리컬 캐시
   *
   * 문제: QuizGenerateItem.standard는 코드만 저장 (string[])
   * 해결: 생성 당시의 전체 AchievementStandard 객체를 누적 보관
   *
   * 예시:
   * 1. 성취기준 [A, B] 선택 → 퀴즈 생성 (item.standard = ['A'])
   * 2. 성취기준 [C, D]로 변경 → 재생성 (item.standard = ['C'])
   * 3. historicalStandards = [A, B, C, D] → 모든 아이템이 매핑 가능
   */
  const [historicalStandards, setHistoricalStandards] = useState<AchievementStandard[]>([]);
  useEffect(() => {
    setHistoricalStandards(prev => unionBy(prev, achievementStandards, standard => standard.code));
  }, [achievementStandards]);
  const hasItems = generatedItems.length > 0;
  const isRegeneratingEmpty = useMemo(() => generateStatus === 'regenerating' && !hasItems, [generateStatus, hasItems]);

  // Case 1: 재생성 중이고 아이템 없음
  if (isRegeneratingEmpty) {
    return <>{pendingFallback}</>;
  }

  // Case 2: 아이템 없음
  if (!hasItems) {
    return <BlankResult />;
  }

  // Case 3: 아이템 있음
  return <BuildAiGeneratedResult ref={ref} resultDescription={<GenerateResultDescription generatedItemsCount={generatedItems.length} />} selectedItems={selection.selectedItems} savedItems={selection.savedItems} unSavedItems={selection.unSavedItems} onSelect={selection.onSelect} onSelectAll={selection.onSelectAll} onDelete={selection.onDelete} skeletonCount={quizCount} status={generateStatus} className={className} selectedAchievementStandards={historicalStandards} />;
});
BuildAiResultSection.displayName = 'BuildAiResultSection';